// Library Methods
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

// Constants
import {
  FAR_TEST_DISTANCE_M,
  NEAR_TEST_DISTANCE_CM,
} from "src/constants/enums";

export type DistanceKindType = "far" | "near";

/**
 * The distance a card or column was measured at, as a chip beside its title.
 *
 * A chip rather than a parenthesis in the heading, because the distance is a property of the
 * measurement rather than part of what the measurement is called -- and because the far figure
 * carried no distance at all when it was written into the heading.
 */
export const DistanceTag = ({ distance }: { distance: DistanceKindType }) => {
  const { t } = useTranslation();

  return (
    <Box
      component="span"
      data-testid={`synoptophore-distance-tag-${distance}`}
      sx={{
        fontSize: "10px",
        fontWeight: 600,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        border: "1px solid currentColor",
        borderRadius: "3px",
        padding: "1px 5px",
        whiteSpace: "nowrap",
        color: distance === "far" ? "#3179FE" : "#E2772E",
      }}
    >
      {distance === "far"
        ? t("Distance · {{distance}} m", { distance: FAR_TEST_DISTANCE_M })
        : t("Near · {{distance}} cm", { distance: NEAR_TEST_DISTANCE_CM })}
    </Box>
  );
};

type FigureColumnType = {
  title: string;
  distance?: DistanceKindType;
  children: ReactNode;
};

/**
 * One column of a card: a title, an optional distance chip, and whatever the column shows.
 *
 * The title block is laid out so a column whose title wraps to two lines does not push its body
 * below its neighbours'. Columns live in a CSS grid with two rows -- the title and the body --
 * and each column spans both via `grid-template-rows: subgrid`, so every title block is as tall
 * as the tallest one and no taller.
 */
export const FigureColumn = ({
  title,
  distance,
  children,
}: FigureColumnType) => (
  <Box
    sx={{
      minWidth: 0,
      border: "1px solid #5B6D7A40",
      borderRadius: "4px",
      padding: "8px",
      display: "grid",
      gridTemplateRows: "subgrid",
      gridRow: "span 2",
      rowGap: "6px",
      // Without subgrid the title block still needs a floor, or a one-line title and a
      // two-line one start their bodies at different heights.
      "@supports not (grid-template-rows: subgrid)": {
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        "& > :first-of-type": { minHeight: "33px" },
      },
    }}
  >
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        alignSelf: "start",
        gap: "3px",
      }}
    >
      <Typography
        fontSize="12px"
        fontWeight={700}
        color="#5B6D7A"
        sx={{
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          lineHeight: 1.35,
        }}
      >
        {title}
      </Typography>
      {distance && <DistanceTag distance={distance} />}
    </Box>

    <Box
      sx={{
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: "4px",
      }}
    >
      {children}
    </Box>
  </Box>
);

/**
 * The grid the columns sit in. Two rows, so each column can span them with subgrid and every
 * title line up with every other.
 */
export const FigureColumns = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      display: "grid",
      gap: "8px",
      gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
      gridTemplateRows: "auto auto",
    }}
  >
    {children}
  </Box>
);
